import { useEffect, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import { arrayMove } from '@dnd-kit/sortable'
import { createPortal } from 'react-dom'

import { BlockContent } from '~/components/BlockContent'
import bootcampStyle from '~/shared/styles/BootcampSanityBlock.module.css'

import { BlockContent as BlockContentType } from '~/bootcamp/Activities/types/activities.types'

import { Column, Task, TaskStatus, OrderElementsAnswerType } from '../types/OrderElements.types'

import { getColumnsData, getTasksData } from '../types/OrderElements.data'

import TaskCard from './TaskCard'

import ColumnContainer from './ColumnContainer'

interface DragNSortProps {
  question: BlockContentType[]
  setFeedBackMessage: (feedback: OrderElementsAnswerType | null) => void
  taskStatus: TaskStatus
  setTaskStatus: (status: TaskStatus) => void
}

function OrderElementsApp({
  setFeedBackMessage,
  question,
  taskStatus,
  setTaskStatus,
}: DragNSortProps): React.ReactElement {
  const [columns, setColumns] = useState<Column[]>(getColumnsData())

  const [tasks, setTasks] = useState<Task[]>(getTasksData())

  const [activeColumn, setActiveColumn] = useState<Column | null>(null)

  const [activeTask, setActiveTask] = useState<Task | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  )

  const readyToCheck = tasks?.filter((task) => task.categoryName === 'question').length < 1

  useEffect(() => {
    if (readyToCheck && taskStatus === TaskStatus.Unchecked) {
      setTaskStatus(TaskStatus.ReadyToCheck)
    }

    if (taskStatus === TaskStatus.Checking) {
      tasks.map((task): Task => {
        if (task.categoryName === 'answer' && task.position === task.id) task.taskStatus = TaskStatus.Right
        else task.taskStatus = TaskStatus.Wrong
        task.check = true
        return task
      })

      const needRetry = tasks.filter((task) => task.taskStatus !== TaskStatus.Right).length > 0

      if (needRetry) setTaskStatus(TaskStatus.Wrong)
      else setTaskStatus(TaskStatus.Right)

      setFeedBackMessage(needRetry ? OrderElementsAnswerType.SELECT_ALL_CORRECT : OrderElementsAnswerType.CORRECT)
    } else if (taskStatus === TaskStatus.Reset) {
      setTaskStatus(TaskStatus.Checking)
      tasks.map((task): Task => {
        task.check = false
        task.categoryName = 'question'
        task.taskStatus = TaskStatus.Unchecked
        return task
      })
      setTasks(
        // eslint-disable-next-line fp/no-mutating-methods
        tasks
          .map((value: Task) => ({ value, sort: Math.random() }))
          .sort((a: { sort: number }, b: { sort: number }) => a.sort - b.sort)
          .map(({ value }: { value: Task }, index: number) => ((value.position = index + 1), value)),
      )

      setTaskStatus(TaskStatus.Unchecked)
      setFeedBackMessage(null)
    }
  }, [readyToCheck, setFeedBackMessage, setTaskStatus, taskStatus, tasks])

  return (
    <div>
      <div
        className={`m-auto ${
          taskStatus === TaskStatus.Right || taskStatus === TaskStatus.Wrong ? 'h-130' : 'h-125'
        } w-full overflow-y-hidden pt-12`}
      >
        <div className="text-black mb-8">
          <BlockContent
            blocks={question}
            className={bootcampStyle['bootcamp-block-content']}
            renderContainerOnSingleChild
          />
        </div>
        <DndContext onDragEnd={onDragEnd} onDragOver={onDragOver} onDragStart={onDragStart} sensors={sensors}>
          <div className="flex flex-col space-y-24 md:space-y-36 pt-8 ">
            <div className="flex gap-2">
              <div className="flex gap-2 w-full min-h-24 touch-none justify-center content-center">
                {columns.map(
                  (col, index) =>
                    col.categoryName === 'answer' &&
                    index - 5 < tasks.length && (
                      <div className="w-1/4 h-full content-center justify-center flex" key={col.id}>
                        <ColumnContainer
                          column={col}
                          containerId={col.id}
                          key={col.id}
                          tasks={tasks?.filter(
                            (task) => task.categoryName === 'answer' && task.position === col.position,
                          )}
                        />
                      </div>
                    ),
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex gap-2 justify-center w-full max-h-20 touch-none">
                {columns.map(
                  (col, index) =>
                    col.categoryName === 'question' &&
                    index < tasks.length && (
                      <div className="w-1/4 content-center justify-center flex" key={col.id}>
                        <ColumnContainer
                          column={col}
                          containerId={col.id}
                          key={col.id}
                          tasks={tasks?.filter(
                            (task) => task.categoryName === 'question' && task.position === col.position,
                          )}
                        />
                      </div>
                    ),
                )}
              </div>
            </div>
          </div>
          {/* The overlay is portaled to document.body with dnd-kit's default z-index of 999, which the
              activity modal (z-modal-over-topbar) paints over - the dragged card has to outrank it. */}
          {typeof document !== 'undefined' &&
            createPortal(
              <DragOverlay className="!z-top">
                {activeColumn && (
                  <ColumnContainer
                    column={activeColumn}
                    containerId={activeColumn.id}
                    tasks={tasks.filter(
                      (task) =>
                        task.categoryName === activeColumn.categoryName && task.position === activeColumn.position,
                    )}
                  />
                )}
                {activeTask && <TaskCard task={activeTask} />}
              </DragOverlay>,
              document.body,
            )}
        </DndContext>
      </div>
    </div>
  )

  function onDragStart(event: DragStartEvent): void {
    if (event.active.data.current?.type === 'Column') {
      setActiveColumn(event.active.data.current.column)
      return
    }

    if (event.active.data.current?.type === 'Task') {
      setActiveTask(event.active.data.current.task)
      return
    }
  }

  function onDragEnd(event: DragEndEvent): void {
    setActiveColumn(null)
    setActiveTask(null)

    const { active, over } = event
    if (!over) return

    const activeId = active.id
    const overId = over.id

    if (activeId === overId) return

    const isActiveAColumn = active.data.current?.type === 'Column'
    if (!isActiveAColumn) return

    setColumns((cols) => {
      const activeColumnIndex = cols.findIndex((col) => col.id === activeId)

      const overColumnIndex = cols.findIndex((col) => col.id === overId)

      return arrayMove(cols, activeColumnIndex, overColumnIndex)
    })
  }

  function onDragOver(event: DragOverEvent): void {
    const { active, over } = event
    if (!over) return

    const activeId = active.id

    const isActiveATask = active.data.current?.type === 'Task'

    const isOverAColumn = over.data.current?.type === 'Column'

    if (!isActiveATask && !isOverAColumn) return

    if (isActiveATask && isOverAColumn) {
      setTasks((e) => {
        const activeIndex = e.findIndex((t) => t.id === activeId)

        if (
          e.find(
            (t) =>
              t.categoryName === over.data.current?.column.categoryName &&
              t.position === over.data.current?.column.position,
          )
        ) {
          return e
        }

        e[activeIndex].categoryName = over.data.current?.column.categoryName
        e[activeIndex].position = over.data.current?.column.position

        const movedTasks = arrayMove(tasks, activeIndex, activeIndex)
        return movedTasks
      })
    }
  }
}

export default OrderElementsApp
